// Thin fetch wrappers around /api/feedback (see server/feedback.js and
// server/index.js's routes) - every call attaches the device id
// (core/device-id.js) that server-side ownership checks are keyed on. Unlike
// core/metrics.js's fire-and-forget track(), these are real user actions
// (submit, edit, delete, vote) so each one awaits its response and throws
// with the server's own error message on failure, for app.js's UI code to
// catch and show.
import { getDeviceId } from './device-id.js';

async function apiCall(path, options = {}) {
    const res = await fetch(path, {
        ...options,
        headers: { 'Content-Type': 'application/json', ...(options.headers || {}) }
    });
    if (res.status === 204) return null;
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `request failed (${res.status})`);
    return data;
}

export async function listFeedback({ type, sort, closed } = {}) {
    const params = new URLSearchParams({ deviceId: getDeviceId() });
    if (type) params.set('type', type);
    if (sort) params.set('sort', sort);
    if (closed) params.set('closed', '1');
    const { items } = await apiCall(`/api/feedback?${params}`);
    return items;
}

// What happened (status change, reply, close/reopen) to this device's own
// items after event id `after` - see server/feedback.js's getUpdatesFor.
export async function getFeedbackUpdates(after = 0) {
    const params = new URLSearchParams({ deviceId: getDeviceId(), after: String(after) });
    return apiCall(`/api/feedback/updates?${params}`);
}

export async function getFeedbackItem(id) {
    const params = new URLSearchParams({ deviceId: getDeviceId() });
    const { item } = await apiCall(`/api/feedback/${id}?${params}`);
    return item;
}

export async function createFeedbackItem({ type, title, body }) {
    const { item } = await apiCall('/api/feedback', {
        method: 'POST',
        body: JSON.stringify({ type, title, body, deviceId: getDeviceId() })
    });
    return item;
}

export async function updateFeedbackItem(id, { type, title, body }) {
    const { item } = await apiCall(`/api/feedback/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ type, title, body, deviceId: getDeviceId() })
    });
    return item;
}

export async function deleteFeedbackItem(id) {
    await apiCall(`/api/feedback/${id}`, {
        method: 'DELETE',
        body: JSON.stringify({ deviceId: getDeviceId() })
    });
}

export async function voteFeedbackItem(id, direction) {
    const { item } = await apiCall(`/api/feedback/${id}/vote`, {
        method: 'POST',
        body: JSON.stringify({ deviceId: getDeviceId(), direction })
    });
    return item;
}

// Any device can reply to any item, not just its own - see
// server/feedback.js's createReply(). Each of these returns the whole
// parent item (with its updated replies array), not just the reply.
export async function createReply(itemId, body) {
    const { item } = await apiCall(`/api/feedback/${itemId}/replies`, {
        method: 'POST',
        body: JSON.stringify({ body, deviceId: getDeviceId() })
    });
    return item;
}

export async function updateReply(itemId, replyId, body) {
    const { item } = await apiCall(`/api/feedback/${itemId}/replies/${replyId}`, {
        method: 'PATCH',
        body: JSON.stringify({ body, deviceId: getDeviceId() })
    });
    return item;
}

export async function deleteReply(itemId, replyId) {
    const { item } = await apiCall(`/api/feedback/${itemId}/replies/${replyId}`, {
        method: 'DELETE',
        body: JSON.stringify({ deviceId: getDeviceId() })
    });
    return item;
}
